<template>
  <div
    :id="element.id + ''"
    class="px-2 py-1v border border-blue-france-sun-113 rounded-md bg-blue-france-975 w-full flex items-center"
  >
    <AbsenceIndicator
      v-if="element.absence"
      :name="element.name"
      :absence="element.absence"
      :civility="element.civility"
      class="mr-1"
    />
    <PTooltip
      :label="`${element.mail} - ${element.name}`"
      activatorWrapperClasses="w-full whitespace-nowrap overflow-hidden text-ellipsis"
      class="grow w-0 [&_.bg-grey-425]:max-w-[90%]"
      :delay="500"
      :offset="5"
    >
      <p class="font-bold inline">{{ element.name }}</p>
    </PTooltip>
    <PButton
      v-if="writable && element.position > 1"
      :data-sentry-id="`MoveElUpBtn-${element.id}`"
      @click="emit('up')"
      size="icon"
      mode="tertiary-borderless"
      :aria-label="`déplacer en amont ${element.name}`"
      class="sr-only focus:not-sr-only"
    >
      <OhVueIcon :name="upIcon" scale="2" />
    </PButton>
    <PButton
      v-if="writable && !isLast"
      @click="emit('down')"
      :data-sentry-id="`MoveElDownBtn-${element.id}`"
      size="icon"
      mode="tertiary-borderless"
      :aria-label="`déplacer en aval ${element.name}`"
      class="sr-only focus:not-sr-only"
    >
      <OhVueIcon :name="downIcon" scale="2" />
    </PButton>
    <OhVueIcon
      v-if="writable"
      :name="dragIcon"
      class="ml-auto mr-2 text-blue-france-sun-113 drag-handle cursor-move"
      scale="1.2"
    />

    <PTooltip v-if="isCurrent" :label="`Ajouter ${element.name} en bas du flux`" :delay="500" :offset="5">
      <PButton
        size="icon"
        mode="tertiary-borderless"
        :data-sentry-id="`AddEndStreamBtn-${element.id}`"
        :aria-label="`Ajouter ${element.name} en bas du flux`"
        @click="emit('addToEndStream')"
      >
        <OhVueIcon :name="addUserIcon" class="text-success" />
      </PButton>
    </PTooltip>

    <PTooltip :label="`Supprimer ${element.name} du flux`" :delay="500">
      <PButton
        v-if="writable"
        :data-sentry-id="`DeleteElBtn-${element.id}`"
        size="icon"
        mode="tertiary-borderless"
        @click="emit('delete')"
        class="delete-btn"
        :aria-label="`Supprimer ${element.name} du flux`"
        color="error"
      >
        <OhVueIcon :name="deleteUserIcon" />
      </PButton>
    </PTooltip>
  </div>
</template>

<script setup lang="ts">
import { type FluxElementDto } from "@/app/dto/flux-element.dto";
import AbsenceIndicator from "@/app/views/LetterFileForm/Flux/AbsenceIndicator.vue";
import { PButton, PTooltip, useIcons } from "@pasta/box-v2";
import { OhVueIcon } from "oh-vue-icons";
import {
  MdDraghandle,
  RiArrowDropDownLine,
  RiArrowDropUpLine,
  RiUserAddFill,
  RiUserUnfollowFill,
} from "oh-vue-icons/icons";

interface Props {
  element: FluxElementDto;
  isLast: boolean;
  writable: boolean;
  isCurrent: boolean;
}

withDefaults(defineProps<Props>(), { writable: false });
const emit = defineEmits<{
  (e: "delete"): void;
  (e: "up"): void;
  (e: "down"): void;
  (e: "addToEndStream"): void;
}>();

const [deleteUserIcon, addUserIcon, dragIcon, upIcon, downIcon] = useIcons(
  RiUserUnfollowFill,
  RiUserAddFill,
  MdDraghandle,
  RiArrowDropUpLine,
  RiArrowDropDownLine
);
</script>
